import { useState } from "react";
import { ChevronRight, Plus } from "lucide-react";
import {
  useGetIdentity,
  useGetList,
  useRecordContext,
  useStore,
  useTranslate,
} from "ra-core";
import { Button } from "@/components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useRealtimeResource } from "@/hooks/useRealtimeResource";
import { cn } from "@/lib/utils";

import { Task } from "../tasks/Task";
import { TaskCreate } from "../tasks/TaskCreate";
import type { Contact, Task as TData } from "../types";

type Scope = "everyone" | "mine";

const PREVIEW_COUNT = 4;
const PAGE_SIZE = 100;

// Every second row is tinted, as on the Tasks page (LCCRM-86). The negative
// margin lets the tint reach past the text without moving the text itself.
const ROW_CLASS =
  "py-3 px-2 -mx-2 rounded-sm even:bg-muted/40 dark:even:bg-muted/25";

// Tasks about one contact, near the top of the Contact page: the open ones,
// then the completed ones behind a disclosure.
// Bill's brief item 4: opening a contact should answer "what do I need to do
// with this person?". Tasks stay assigned to teammates; contact_id only links
// them to the person they are about. LCATOMICZO-31.
export const ContactActionItems = ({ className }: { className?: string }) => {
  const record = useRecordContext<Contact>();
  const translate = useTranslate();
  const { identity } = useGetIdentity();
  const [scope, setScope] = useStore<Scope>(
    "contacts.actionItems.scope",
    "everyone",
  );
  // Remembered per scope, so switching scope always starts collapsed.
  const [expandedScope, setExpandedScope] = useState<Scope | null>(null);
  const expanded = expandedScope === scope;
  const [addOpen, setAddOpen] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);

  // Plane-side completions, teammates' edits and AssigneePill's optimistic
  // reassign all reconcile through this subscription.
  useRealtimeResource("tasks");

  const mine = scope === "mine";
  const { data, total, isPending, error } = useGetList<TData>(
    "tasks",
    {
      pagination: { page: 1, perPage: PAGE_SIZE },
      // Postgres sorts ascending with NULLS LAST: overdue, soonest, undated.
      sort: { field: "due_date", order: "ASC" },
      filter: {
        contact_id: record?.id,
        "done_date@is": null,
        ...(mine ? { sales_id: identity?.id } : {}),
      },
    },
    { enabled: record?.id != null && (!mine || identity?.id != null) },
  );

  // Done tasks about this contact. Bill ticked a task here, watched it leave
  // the list, and took that for a delete: "the only way to mark a task as done
  // is to delete it". They now go under "Completed", where unticking one
  // brings it back. LCCRM-86.
  const { data: doneData, total: doneTotal } = useGetList<TData>(
    "tasks",
    {
      pagination: { page: 1, perPage: PAGE_SIZE },
      sort: { field: "done_date", order: "DESC" },
      filter: {
        contact_id: record?.id,
        "done_date@not.is": null,
        ...(mine ? { sales_id: identity?.id } : {}),
      },
    },
    { enabled: record?.id != null && (!mine || identity?.id != null) },
  );

  if (!record) return null;

  const tasks = data ?? [];
  const count = total ?? tasks.length;
  const visible = expanded ? tasks : tasks.slice(0, PREVIEW_COUNT);
  const doneTasks = doneData ?? [];
  const doneCount = doneTotal ?? doneTasks.length;

  return (
    <section className={className} aria-labelledby="contact-action-items">
      <div className="flex items-center gap-2 mb-2">
        <h2 id="contact-action-items" className="text-base font-semibold">
          {translate("resources.contacts.action_items.title")}
        </h2>
        {!isPending && !error && count > 0 && (
          <span className="text-sm text-muted-foreground tabular-nums">
            {count}
          </span>
        )}
        <ToggleGroup
          type="single"
          size="sm"
          variant="outline"
          className="ml-auto"
          value={scope}
          // Radix sends "" when the active item is clicked again; keep the scope.
          onValueChange={(value) => value && setScope(value as Scope)}
        >
          <ToggleGroupItem value="everyone" className="px-3 text-xs">
            {translate("resources.contacts.action_items.everyone")}
          </ToggleGroupItem>
          <ToggleGroupItem value="mine" className="px-3 text-xs">
            {translate("resources.contacts.action_items.mine")}
          </ToggleGroupItem>
        </ToggleGroup>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 px-2 text-xs"
          onClick={() => setAddOpen(true)}
        >
          <Plus className="w-3.5 h-3.5" />
          {translate("resources.contacts.action_items.add")}
        </Button>
      </div>

      <TaskCreate
        open={addOpen}
        onOpenChange={setAddOpen}
        contactId={record.id}
      />

      {isPending ? null : error ? (
        <p className="text-sm text-muted-foreground">
          {translate("resources.contacts.action_items.load_error")}
        </p>
      ) : tasks.length === 0 ? (
        <div className="flex flex-col items-start gap-2">
          <p className="text-sm text-muted-foreground">
            {translate(
              mine
                ? "resources.contacts.action_items.empty_mine"
                : "resources.contacts.action_items.empty_everyone",
            )}
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setAddOpen(true)}
          >
            <Plus className="w-4 h-4" />
            {translate("resources.contacts.action_items.add_first")}
          </Button>
        </div>
      ) : (
        <>
          <div className="divide-y">
            {visible.map((task) => (
              <div key={task.id} className={ROW_CLASS}>
                <Task task={task} hideContact />
              </div>
            ))}
          </div>
          {tasks.length > PREVIEW_COUNT && (
            <Button
              type="button"
              variant="link"
              size="sm"
              className="px-0 mt-1 h-auto"
              onClick={() => setExpandedScope(expanded ? null : scope)}
            >
              {expanded
                ? translate("resources.contacts.action_items.show_fewer")
                : translate("resources.contacts.action_items.view_all", {
                    smart_count: count,
                  })}
            </Button>
          )}
          {expanded && count > tasks.length && (
            <p className="text-xs text-muted-foreground mt-1">
              {translate("resources.contacts.action_items.truncated")}
            </p>
          )}
        </>
      )}

      {/* Shown with no open task too, so a ticked task always has somewhere
          visible to go. A list, not the open list's divs, so the two stay
          distinct. */}
      {doneCount > 0 && (
        <div className="mt-3">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 -ml-2 px-2 gap-1 text-xs text-muted-foreground cursor-pointer"
            aria-expanded={showCompleted}
            onClick={() => setShowCompleted(!showCompleted)}
          >
            <ChevronRight
              className={cn(
                "size-3.5 transition-transform",
                showCompleted && "rotate-90",
              )}
            />
            {translate("resources.contacts.action_items.completed", {
              smart_count: doneCount,
            })}
          </Button>
          {showCompleted && (
            <ul
              className="mt-2 divide-y"
              aria-label={translate(
                "resources.contacts.action_items.completed",
                { smart_count: doneCount },
              )}
            >
              {doneTasks.map((task) => (
                <li key={task.id} className={ROW_CLASS}>
                  <Task task={task} hideContact />
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
};
